// The pieces a workspace page is built from (Mitch, 2026-11-03: "The projects view is still messy, using a lot of bubble
// containers … Getting into a run is also messy and it's not very coherent"), so every tab reads the same way:
// - `Figures` + `Figure`: a page's key numbers in one strip, label over value, no boxes (Consignment's strip; never a
//   grid of tiles).
// - `PageSection`: a plain heading (title, a note that takes the room left, the table's View and Columns menus, actions)
//   over its content: a `DataTable fit` (its own border is the box) or, `boxed`, anything else in one bordered box.
//   Never a card with a table boxed inside it.
import { type ReactNode, useState } from "react";
import { cx } from "./controls.tsx";

/** One figure in a summary strip: its label over its value, with a few words after the value. */
export function Figure({
  label,
  value,
  sub,
  tone,
  title,
}: {
  label: string;
  value: ReactNode;
  sub?: ReactNode;
  tone?: "warn" | "danger" | "ok";
  title?: string;
}) {
  return (
    <div className="flex min-w-0 flex-col" title={title}>
      <span className="text-[11px] font-medium tracking-wide whitespace-nowrap text-faint uppercase">{label}</span>
      <span className="flex min-w-0 items-baseline gap-1.5 whitespace-nowrap">
        <span
          className={cx("font-mono text-[15px] font-semibold", tone === "warn" && "text-warn", tone === "danger" && "text-danger", tone === "ok" && "text-ok")}
        >
          {value}
        </span>
        {sub && <span className="min-w-0 truncate text-[12px] text-muted">{sub}</span>}
      </span>
    </div>
  );
}

/** A page's key numbers in one strip (they wrap on a narrow window); `children` are `Figure`s, then anything after them. */
export function Figures({ children, className }: { children: ReactNode; className?: string }) {
  return <div className={cx("flex flex-wrap items-end gap-x-8 gap-y-2 px-1", className)}>{children}</div>;
}

/**
 * A section of a page: its heading row, then its content. A function child gets the heading's slot for a table's View and
 * Columns menus (`viewsSlot`), so they sit by the title. `boxed` puts content that isn't a table in one bordered box.
 */
export function PageSection({
  title,
  note,
  actions,
  foot,
  boxed,
  boxClassName,
  className,
  children,
}: {
  title: ReactNode;
  note?: ReactNode;
  actions?: ReactNode;
  foot?: ReactNode;
  boxed?: boolean;
  /** The box's own classes: `w-fit max-w-full` for a narrow grid (units per variant) that shouldn't stretch. */
  boxClassName?: string;
  className?: string;
  children: ReactNode | ((viewsSlot: HTMLDivElement | null) => ReactNode);
}) {
  const [slot, setSlot] = useState<HTMLDivElement | null>(null);
  const body = typeof children === "function" ? children(slot) : children;
  return (
    <section className={cx("flex min-w-0 flex-col gap-1.5", className)}>
      <div className="flex min-h-8 items-center gap-2 px-1">
        <h2 className="min-w-0 truncate font-semibold">{title}</h2>
        {/* The note takes only the room left (no width of its own), so a long note never cuts a short title */}
        {note && (
          <span className="min-w-0 flex-1 basis-0 truncate text-[12px] text-muted" title={typeof note === "string" ? note : undefined}>
            {note}
          </span>
        )}
        <div ref={setSlot} className={cx("flex shrink-0 items-center gap-1", !note && "ml-auto")} />
        {actions && <div className="flex shrink-0 items-center gap-2">{actions}</div>}
      </div>
      {boxed ? <div className={cx("min-w-0 rounded-md border border-line bg-surface", boxClassName)}>{body}</div> : body}
      {foot && <div className="px-1 text-[11px] text-faint">{foot}</div>}
    </section>
  );
}
